<h2 mat-dialog-title>
  <ng-container [ngSwitch]="step">
    <ng-container *ngSwitchCase="'search'">Add contact</ng-container>
    <ng-container *ngSwitchCase="'link'">Link {{ picked?.name || 'contact' }}</ng-container>
    <ng-container *ngSwitchDefault>{{ isEdit ? 'Edit contact' : 'New contact' }}</ng-container>
  </ng-container>
</h2>

<mat-dialog-content>

  <!-- 1. Search the contact book -->
  <div *ngIf="step === 'search'" class="cb-pane">
    <mat-form-field appearance="outline" class="w-100 cb-search">
      <mat-label>Search by name, email, phone or customer</mat-label>
      <mat-icon matPrefix>search</mat-icon>
      <input matInput [(ngModel)]="search" (ngModelChange)="filter()" cdkFocusInitial autocomplete="off">
    </mat-form-field>

    <div class="cb-list">
      <button type="button" *ngFor="let x of results" class="cb-item" [class.cb-item--here]="isLinkedHere(x)"
              [disabled]="isLinkedHere(x)" (click)="pick(x)">
        <div class="cb-item-main">
          <div class="kk-contact-name">{{ x.name || 'No name' }}<span *ngIf="x.stilling" class="kk-muted"> · {{ x.stilling }}</span></div>
          <div class="kk-small kk-muted">{{ x.email }}<span *ngIf="x.email && x.phone"> · </span>{{ x.phone }}</div>
          <div class="kk-small kk-muted" *ngIf="x.cards.length">{{ x.cards.join(', ') }}</div>
        </div>
        <span class="kk-chip" *ngIf="isLinkedHere(x)">Already on this customer</span>
        <mat-icon *ngIf="!isLinkedHere(x)" class="kk-muted">add_link</mat-icon>
      </button>
      <p class="kk-empty-line" *ngIf="!results.length">No contact matches “{{ search }}”.</p>
    </div>
  </div>

  <!-- 2. Link an existing person -->
  <div *ngIf="step === 'link'" class="cb-pane">
    <div class="kk-contact cb-picked">
      <div class="kk-contact-main">
        <div class="kk-contact-name">{{ picked.name }}<span *ngIf="picked.stilling" class="kk-muted"> · {{ picked.stilling }}</span></div>
        <div class="kk-contact-lines">
          <span *ngIf="picked.email">{{ picked.email }}</span>
          <span *ngIf="picked.phone">{{ picked.phone }}</span>
        </div>
        <div class="kk-small kk-muted" *ngIf="picked.cards.length">Also on: {{ picked.cards.join(', ') }}</div>
      </div>
    </div>
    <mat-form-field appearance="outline" class="w-100 mt-3">
      <mat-label>Roles on this customer</mat-label>
      <mat-select [(ngModel)]="linkRoles" multiple>
        <mat-option *ngFor="let role of roleOptions" [value]="role">{{ role }}</mat-option>
      </mat-select>
    </mat-form-field>
  </div>

  <!-- 3. Create / edit the person -->
  <form *ngIf="step === 'form'" [formGroup]="contactsForm" class="cb-pane">
    <p class="kk-small kk-muted" *ngIf="isEdit && otherCardCount">
      <mat-icon class="cb-inline-icon">info</mat-icon>
      Also on {{ otherCardCount }} other customer{{ otherCardCount === 1 ? '' : 's' }} — name, phone, email and position change there too.
    </p>
    <div class="row">
      <div class="col-md-4">
        <mat-form-field appearance="outline" class="w-100">
          <mat-label>Name</mat-label>
          <input matInput formControlName="name">
        </mat-form-field>
      </div>
      <div class="col-md-4">
        <mat-form-field appearance="outline" class="w-100">
          <mat-label>Phone</mat-label>
          <input matInput formControlName="phone">
        </mat-form-field>
      </div>
      <div class="col-md-4">
        <mat-form-field appearance="outline" class="w-100">
          <mat-label>Email</mat-label>
          <input matInput formControlName="email" (blur)="checkEmail()">
          <mat-error *ngIf="contactsForm.get('email')?.hasError('email')">Must be a valid email.</mat-error>
        </mat-form-field>
      </div>
    </div>

    <div class="cb-warn" *ngIf="emailMatch">
      <span><strong>{{ emailMatch.name }}</strong> already has this email<span *ngIf="emailMatch.cards.length"> ({{ emailMatch.cards.join(', ') }})</span>.</span>
      <button mat-stroked-button type="button" *ngIf="kundekortId && !isLinkedHere(emailMatch)" (click)="pick(emailMatch)">Link instead</button>
    </div>

    <div class="row">
      <div class="col-md-6">
        <mat-form-field appearance="outline" class="w-100">
          <mat-label>Position</mat-label>
          <input matInput formControlName="stilling">
        </mat-form-field>
      </div>
      <div class="col-md-6" *ngIf="kundekortId">
        <mat-form-field appearance="outline" class="w-100">
          <mat-label>Roles on this customer</mat-label>
          <mat-select formControlName="roles" multiple>
            <mat-option *ngFor="let role of roleOptions" [value]="role">{{ role }}</mat-option>
          </mat-select>
        </mat-form-field>
      </div>
    </div>

    <!-- Customers this person is linked to (Contacts page) -->
    <ng-container *ngIf="isEdit && !kundekortId">
      <h5 class="kk-h5-gap">Customers</h5>
      <div class="kk-chips cb-cards">
        <span class="kk-chip cb-card" *ngFor="let c of linkedCards">
          {{ c.name }}
          <button type="button" class="cb-chip-x" aria-label="Remove" (click)="removeCard(c)"><mat-icon>close</mat-icon></button>
        </span>
        <span class="kk-muted kk-small" *ngIf="!linkedCards.length">Not linked to any customer.</span>
      </div>
      <mat-form-field appearance="outline" class="w-100 mt-2">
        <mat-label>Link to customer…</mat-label>
        <input matInput [(ngModel)]="cardSearch" [ngModelOptions]="{ standalone: true }" (ngModelChange)="filterCards()"
               [matAutocomplete]="cardAuto" autocomplete="off">
        <mat-autocomplete #cardAuto="matAutocomplete" (optionSelected)="addCard($event.option.value)">
          <mat-option *ngFor="let c of cardOptions" [value]="c">{{ c.name }}</mat-option>
        </mat-autocomplete>
      </mat-form-field>
    </ng-container>
  </form>
</mat-dialog-content>

<mat-dialog-actions align="end">
  <ng-container [ngSwitch]="step">
    <ng-container *ngSwitchCase="'search'">
      <button mat-button mat-dialog-close>Close</button>
      <button mat-flat-button color="primary" (click)="startNew()"><mat-icon>person_add</mat-icon> New contact</button>
    </ng-container>
    <ng-container *ngSwitchCase="'link'">
      <button mat-button (click)="step = 'search'">Back</button>
      <button mat-flat-button color="primary" [disabled]="saving" (click)="linkPicked()"><mat-icon>add_link</mat-icon> Link contact</button>
    </ng-container>
    <ng-container *ngSwitchDefault>
      <button mat-button *ngIf="!isEdit" (click)="step = 'search'">Back</button>
      <button mat-button mat-dialog-close *ngIf="isEdit">Close</button>
      <button mat-flat-button color="primary" [disabled]="contactsForm.invalid || saving" (click)="saveContacts()">
        {{ isEdit ? 'Save changes' : 'Save contact' }}
      </button>
    </ng-container>
  </ng-container>
</mat-dialog-actions>
